import React, { useState } from 'react';
import { useNav, useUser } from '../App';
import { Box, TextField, Button, Typography, Paper, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { userAPI } from '../services/api';
import api from '../services/api';
import { C } from '../theme';

function Onboarding() {
  const navigate = useNav();
  const { setUser } = useUser();
  const [step, setStep] = useState(1);
  const [aliasState, setAliasState] = useState({
    formatError: '', isBanned: false, isChecking: false,
    isAvailable: null, message: '', suggestions: []
  });
  const aliasTimer = React.useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    gender: '',
    alias: '',
    lookingFor: '',
    acceptedStandards: false
  });
  const [submitError, setSubmitError] = useState('');

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };
  const handleAliasChange = (val) => {
    if (aliasTimer.current) clearTimeout(aliasTimer.current);
    if (!val) { setAliasState({ formatError: '', isBanned: false, isChecking: false, isAvailable: null, message: '', suggestions: [] }); return; }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(val)) {
      setAliasState(s => ({ ...s, formatError: val.length < 3 ? 'Minimum 3 characters' : val.length > 20 ? 'Max 20 characters' : 'Only letters, numbers, underscore', isAvailable: null, isChecking: false, suggestions: [] }));
      return;
    }
    setAliasState(s => ({ ...s, formatError: '', isChecking: true, isAvailable: null, suggestions: [] }));
    aliasTimer.current = setTimeout(async () => {
      try {
        const r = await fetch((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/auth/check-alias?alias=' + encodeURIComponent(val));
        const data = await r.json();
        setAliasState({ formatError: '', isBanned: data.banned || false, isChecking: false, isAvailable: data.available, message: data.message, suggestions: data.suggestions || [] });
      } catch {
        setAliasState(s => ({ ...s, isChecking: false, isAvailable: null, message: '' }));
      }
    }, 400);
  };

  const handleSubmit = async () => {
    try {
      const phone = localStorage.getItem('temp_phone');

      const response = await userAPI.register({
        phone: phone,
        name: formData.name,
        dob: formData.dob,
        gender: formData.gender,
        alias: formData.alias,
        lookingFor: formData.lookingFor
      });

      if (response.data.token) {
        localStorage.setItem('binko_token', response.data.token);
      }

      // Fetch user with effectiveStatus so App.js routing is correct from the start
      try {
        const meRes = await api.get('/auth/me');
        if (meRes.data.status === 'success') {
          const userData = meRes.data.user;
          setUser(userData);
          navigate(userData.effectiveStatus === 'WAITLISTED' ? '/waitlist' : '/home');
          return;
        }
      } catch (_) {
        // /auth/me failed — fall through to /home; App.js will re-fetch on next load
      }

      navigate('/home');
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };


  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: '#f0f0f5',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.30)' },
      '&:hover fieldset': { borderColor: 'rgba(251,188,21,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#FBBC15' },
    },
    '& .MuiInputLabel-root': { color: '#7a7a8c' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#FBBC15' },
    '& input': { color: '#f0f0f5' },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: C.bgApp, p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%' }}>
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
          Complete Your Profile
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Step {step} of 3
        </Typography>

        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <TextField
              sx={inputSx}
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              margin="normal"
              helperText="Your real name (not displayed publicly)"
            />
            <TextField
              sx={inputSx}
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.dob}
              onChange={(e) => handleChange('dob', e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="You must be 21 or older"
            />
            <TextField
              sx={inputSx}
              fullWidth
              select
              label="Gender"
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              margin="normal"
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={!formData.name || !formData.dob || !formData.gender}
              sx={{ mt: 3 }}
            >
              Next
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Create Your Profile</Typography>
            <TextField
              sx={inputSx}
              fullWidth
              label="Alias (Display Name)"
              value={formData.alias}
              onChange={(e) => {
                const val = e.target.value.replace(/\s/g, '');
                handleChange('alias', val);
                handleAliasChange(val);
              }}
              margin="normal"
              helperText={
                aliasState.formatError ? aliasState.formatError :
                aliasState.isBanned ? aliasState.message :
                aliasState.isChecking ? '⏳ Checking...' :
                aliasState.isAvailable === true ? '✅ Available' :
                aliasState.isAvailable === false ? '❌ ' + aliasState.message :
                'Only letters, numbers, underscore. 3–20 characters.'
              }
              error={
                !!aliasState.formatError ||
                aliasState.isBanned ||
                aliasState.isAvailable === false
              }
              inputProps={{ maxLength: 20 }}
            />
            {aliasState.suggestions.length > 0 && (
              <Box sx={{ mt: 0.5, mb: 1 }}>
                <Typography variant="caption" sx={{ color: C.textMuted }}>Try: </Typography>
                {aliasState.suggestions.map(s => (
                  <Button key={s} size="small" variant="text"
                    sx={{ fontSize: '0.75rem', py: 0, minWidth: 0, mr: 0.5 }}
                    onClick={() => { handleChange('alias', s); handleAliasChange(s); }}>
                    {s}
                  </Button>
                ))}
              </Box>
            )}
            <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mt: 0.5, mb: 1 }}>
              Usernames must follow community guidelines. Any profane, abusive, or inappropriate alias may result in account suspension. The platform reserves the right to remove users with such aliases.
            </Typography>
            <TextField
              sx={inputSx}
              fullWidth
              select
              label="Looking For"
              value={formData.lookingFor}
              onChange={(e) => handleChange('lookingFor', e.target.value)}
              margin="normal"
            >
              <MenuItem value="men">Men</MenuItem>
              <MenuItem value="women">Women</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button fullWidth variant="outlined" onClick={handleBack}>
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleNext}
                disabled={
                  !formData.alias ||
                  !formData.lookingFor ||
                  !!aliasState.formatError ||
                  aliasState.isBanned ||
                  aliasState.isAvailable !== true ||
                  aliasState.isChecking
                }
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>Community Standards</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Before you start, please read and accept our community standards:
            </Typography>
            <Box sx={{ bgcolor: C.bgCard, p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>✓ Respect everyone's boundaries</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✓ Never make anyone feel unsafe</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✓ Be honest about your identity</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✓ Accept "no" gracefully</Typography>
              <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
                ⚠️ We have ZERO TOLERANCE for harassment or making women feel unsafe
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.acceptedStandards}
                  onChange={(e) => handleChange('acceptedStandards', e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: C.textPrimary }}>
                  I agree to these{' '}
                  <a href="/legal/terms" target="_blank" rel="noreferrer"
                     style={{ color: '#FBBC15', textDecoration: 'underline' }}>
                    community standards
                  </a>
                </Typography>
              }
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button fullWidth variant="outlined" onClick={handleBack}>
                Back
              </Button>
              {submitError && (
                <p style={{ color: '#ff4560', fontSize: '13px', textAlign: 'center', margin: '4px 0' }}>{submitError}</p>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={() => { setSubmitError(''); handleSubmit(); }}
                disabled={!formData.acceptedStandards}
              >
                Complete Setup
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default Onboarding;
